import * as Sentry from '@sentry/react';
import { isString } from 'lodash';
import moment from 'moment';
import React from 'react';
import themes from '../themes/themes';
import { School } from '../types/settings';
import {
    LESSON_FILTERS,
    PAYMENT_METHODS,
    SCHOOLS_IDS_BOOKINGFORMATION,
    SCHOOLS_IDS_BS, SCHOOLS_IDS_SSA, SCHOOLS_IDS_SSS,
    SNOWLI_AI_SCHOOL_GROUP_KEYS,
    THEME_VERSIONS,
    VERSIONS,
    VERSIONS_NAMES,
    VERSIONS_URL
} from './constants';

export const getTranslation = (list: any[], lang: string) => {
    const translation = list.find((t) => t.lang === lang);
    if (!translation) return list.find((t) => t.lang === 'en');
    return translation;
};

export const getSchoolsIds = () => {
    switch (getCurrentVersion()) {
        case VERSIONS.SkiSchoolAlliance:
            return SCHOOLS_IDS_SSA;
        case VERSIONS.BookingFormation:
            return SCHOOLS_IDS_BOOKINGFORMATION;
        case VERSIONS.BookingSport:
            return SCHOOLS_IDS_BS;
        case VERSIONS.Swisskischool:
            return SCHOOLS_IDS_SSS;
        default:
            Sentry.captureMessage(`error get_schools_ids : ${getCurrentVersion()}`);
            return SCHOOLS_IDS_SSS;
    }
};
export const domaineBaseHasSubdomainAlready = () => {
    if (process.env.REACT_APP_FRONTEND_DEFAULT_URL?.split('.').length === 3) {
        return ({
            max: 3,
            equal: 4,
            func: (splited: string[]) => {
                if (splited.length > 2) {
                    return `${splited[1]}.${splited[2]}.${splited[3]}`;
                }
                return `${splited[1]}.${splited[2]}`;
            },
            baseDomainFunc: (splited: string[]) => {
                if (splited.length > 3) {
                    return `${splited[1]}.${splited[2]}.${splited[3]}`;
                }
                return `${splited[0]}.${splited[1]}.${splited[2]}`;
            },
            contentFunc: (url: string) => {
                const splitted = url.split('.');
                if (splitted.length > 2) {
                    return `${splitted[1]}.${splitted[2]}`;
                }
                return url;
            }
        });
    }
    return {
        max: 2,
        equal: 3,
        func: (splited: string[]) => `${splited[1]}.${splited[2]}`,
        contentFunc: (url: string) => `${url}`,
        baseDomainFunc: (splited: string[]) => {
            if (splited.length > 2) {
                return `${splited[1]}.${splited[2]}`;
            }
            return `${splited[0]}.${splited[1]}`;
        },
    };
};

export const getDomain = () => {
    const values = domaineBaseHasSubdomainAlready();
    const splited = window.location.hostname.split('.');
    if (splited.length === values.equal) return values.func(splited);
    return window.location.hostname;
};

export const getSubDomain = () => {
    const values = domaineBaseHasSubdomainAlready();
    const splited = window.location.hostname.split('.');
    if (splited.length === values.equal) return splited[0];
    if (splited.length >= values.max && splited[1] === 'localhost') return splited[0];
    return undefined;
};

export const getCurrentVersion = (search_domain: string | undefined = undefined): number => {
    const domain = search_domain || getDomain();

    if (domain.endsWith(VERSIONS_URL.SkiSchoolAlliance)) {
        return VERSIONS.SkiSchoolAlliance;
    } if (domain.endsWith(VERSIONS_URL.BookingSport)) {
        return VERSIONS.BookingSport;
    } if (domain.endsWith(VERSIONS_URL.BookingFormation)) {
        return VERSIONS.BookingFormation;
    } if (domain.endsWith(VERSIONS_URL.Swisskischool)) {
        return VERSIONS.Swisskischool;
    }
    if (search_domain !== undefined) {
        return VERSIONS.Swisskischool;
    }

    Sentry.captureMessage(`error getCurrentVersion : ${domain}`);
    return getCurrentVersion(process.env.REACT_APP_FRONTEND_DEFAULT_URL);
};

export const getAllVersionUrls = () => {
    const values = domaineBaseHasSubdomainAlready();

    const allVersionUrls: string[] = [];
    for (const key in VERSIONS_URL) {
        if (Object.prototype.hasOwnProperty.call(VERSIONS_URL, key)) {
            if (values.max > 2) {
                allVersionUrls.push(values.contentFunc(VERSIONS_URL[key]));
            }
            allVersionUrls.push(`${VERSIONS_URL[key]}`);
        }
    }

    return allVersionUrls;
};
export const getVersionName = () => {
    const version = getCurrentVersion();
    switch (version) {
        case VERSIONS.SkiSchoolAlliance:
            return VERSIONS_NAMES.SkiSchoolAlliance;
        case VERSIONS.BookingFormation:
            return VERSIONS_NAMES.BookingFormation;
        case VERSIONS.BookingSport:
            return VERSIONS_NAMES.BookingSport;
        default:
            return VERSIONS_NAMES.Swisskischool;
    }
};

export const getSchoolById = (id: number) => {
    const school: any = Object.values(getSchoolsIds()).find((v) => v[1] === id);

    return school[0];
};

type FormatMessage = (props: { id: string; }) => string;
export const getActivityNameFromType = (type: string | undefined, formatMessage: FormatMessage) => {
    switch (type) {
        case LESSON_FILTERS.SKIING:
            return formatMessage({ id: 'products.ski' });
        case LESSON_FILTERS.SNOWBOARD:
            return formatMessage({ id: 'products.snowboard' });
        case LESSON_FILTERS.TELEMARK:
            return formatMessage({ id: 'products.telemark' });
        case LESSON_FILTERS.OTHER:
            return formatMessage({ id: 'products.other' });
        case LESSON_FILTERS.CROSSCOUNTRY:
            return formatMessage({ id: 'products.crosscountry' });
        case LESSON_FILTERS.BIATHLON:
            return formatMessage({ id: 'products.biathlon' });
        case LESSON_FILTERS.SKITOURING:
            return formatMessage({ id: 'products.skitouring' });
        case LESSON_FILTERS.SUMMER:
            return formatMessage({ id: 'products.summer' });
        case LESSON_FILTERS.ROCKER:
            return formatMessage({ id: 'products.rocker' });
        case LESSON_FILTERS.MOUNTAINEERING:
            return formatMessage({ id: 'products.mountaineering' });
        case LESSON_FILTERS.ICE:
            return formatMessage({ id: 'products.ice' });
        case LESSON_FILTERS.SNOW:
            return formatMessage({ id: 'products.snow' });
        case LESSON_FILTERS.WINTER:
            return formatMessage({ id: 'products.winter' });
        case LESSON_FILTERS.TRAIL:
            return formatMessage({ id: 'products.trail' });
        case LESSON_FILTERS.FORMATION:
            return formatMessage({ id: 'products.formation' });
        case undefined:
            // Lesson not yet loaded
            return '';
        default:
            Sentry.captureMessage(`Default case, type: ${type}`);
            return type;
    }
};

/**
 * Resolve a school's raw `accepted_payment_methods` (API codes, any order, may
 * contain unknown codes) into the displayable {code,label}[] in canonical order
 * (PAYMENT_METHODS). Unknown codes are ignored; empty/undefined yields []. The
 * match is case-insensitive and trims whitespace.
 */
export const resolvePaymentMethods = (accepted?: string[]) => {
    if (!accepted || accepted.length === 0) return [];
    const set = new Set(accepted.map((m) => String(m).toUpperCase().trim()));
    return PAYMENT_METHODS.filter((pm) => set.has(pm.code));
};

export const getLessonNameFromType = (type: string | undefined, formatMessage: FormatMessage) => {
    switch (type) {
        case LESSON_FILTERS.GROUP_LESSON:
            return formatMessage({ id: 'products.group' });
        case LESSON_FILTERS.PRIVATE_LESSON:
            return formatMessage({ id: 'products.private' });
        case undefined:
            // Lesson not yet loaded
            return '';
        default:
            Sentry.captureMessage(`Error undefined - default case getLessonNameFromType, type: ${type}`);
            return '-';
    }
};

export const decimalToTime = (
    decimal: number,
    formatMessage: FormatMessage,
    dayAwareness: boolean = true,
) => {
    let total = 0;
    if (!dayAwareness) {
        total = Math.floor(decimal);
    } else if (decimal > 24) {
        for (let i = 0; i < decimal; i += 24) {
            total += Math.floor(decimal) % 24;
        }
    } else {
        total = Math.floor(decimal) % 24;
    }

    const minutes: number = Math.round((decimal % 1) * 60);
    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${total}${formatMessage({ id: 'booking.h' })}${minStr}`;
};

export const timeToDecimal = (time: number) => moment.duration(time);

/**
 * If the number has a 0 as the digit after the decimal point
 * @param {Number} number - the number to check and convert
 * @returns the number in the right type
 */
export const toInt = (number: any) => (isString(number) ? parseInt(number, 10) : number);

export const cransParamsToBody = (params: any, formatMessage: FormatMessage) => {
    const body: any = {};
    Object.assign(body, params);
    if (body.lesson_type_in) body.lesson_type_in = body.lesson_type_in.map((value: any) => formatMessage({ id: `crans.${value}` }));
    return body;
};

export const returnRefinedURL = (key: any, url: string) => {
    // separating the key-value ('search') portion of the URL from the rest:
    const urlParts = url.split('?');
    // if we have only a single array-element, or if the key to remove
    // is not found in the URL, we quit here and return the same unchanged URL:
    if (urlParts.length === 1 || url.indexOf(key) === -1) {
        // there were no parameters, or the
        // key wasn't present
        return url;
    }

    // otherwise, we split the key-value string on the '&' characters,
    // for an array of key=value strings:
    const keyValues = urlParts[1].split('&');
    // filtering that array:
    const refinedKeyValues = keyValues.filter((keyValuePair) => keyValuePair.indexOf(key) !== 0).join('&');
    // keeping only those array elements that don't /start with/
    // the key to be removed:
    // joining the key=value pairs back into a string:

    // returning the refined URL:
    return `${urlParts[0]}?${refinedKeyValues}`;
};

export const isURL = (str: string) => {
    const pattern = new RegExp('^(https?:\\/\\/)?' // protocol
        + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
        + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
        + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
        + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
        + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(str);
};

export const getPath = () => {
    const splitted = window.location.href.split('/');
    let path = '/';
    for (let i = 3; i < splitted.length; i++) {
        path += splitted[i];
        if ((i + 1) !== splitted.length) {
            path += '/';
        }
    }
    return path;
};

export const getCurrentLanguage = () => {
    const path = getPath();
    if (path === undefined || path === null || path === '') {
        return 'fr';
    }
    const splitted = path.split('/');

    const lang = splitted[1].toLowerCase();
    if (['fr', 'en', 'it', 'de'].includes(lang)) {
        return lang;
    }
    return 'fr';
};

export interface ImgWithFallbackProps extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
    fallback: string;
    containerClasses?: string;
    type?: string;
}

type Props = ImgWithFallbackProps;

// const defaultProps = {
//     containerClasses: '',
//     type: 'image/webp',
// };

export function ImgWithFallback({
    src,
    fallback,
    containerClasses = '',
    type = 'image/webp',
    ...delegated
}: Props) {
    return (
        <picture className={containerClasses}>
            <source srcSet={src} type={type} />
            {/* eslint-disable-next-line jsx-a11y/alt-text, react/jsx-props-no-spreading */}
            <img src={fallback} {...delegated} />
        </picture>
    );
}

// ImgWithFallback.defaultProps = defaultProps;

export const getCurrentThemeVersion = () => {
    const domain = getDomain();
    const subdomain = getSubDomain();

    if (domain.includes(VERSIONS_URL.SkiSchoolAlliance)) {
        if (subdomain && subdomain.includes('villars')) return THEME_VERSIONS.VillarsSkiSchool;
        if (subdomain && subdomain.includes('diablerets')) return THEME_VERSIONS.DiableretsSkiSchool;
        return THEME_VERSIONS.SkiSchoolAlliance;
    } if (domain.includes(VERSIONS_URL.BookingSport)) {
        if (subdomain && subdomain.includes('ride2peak')) return THEME_VERSIONS.BookingSportRide2Peak;
        if (subdomain && subdomain.includes('evolution')) return THEME_VERSIONS.BookingSportEvolution;
        if (subdomain && subdomain.includes('sommetetneige')) return THEME_VERSIONS.bookingsportSommetEtNeige;
        if (subdomain && subdomain.includes('zenklusen')) return THEME_VERSIONS.BookingsportZenklusen;
        if (subdomain && subdomain.includes('hallenbarter')) return THEME_VERSIONS.BookingsportHallenBarter;
        if (subdomain && subdomain.includes('pratoborni')) return THEME_VERSIONS.BookingsportPratoBorni;
        if (subdomain && subdomain.includes('puretrace')) return THEME_VERSIONS.BookingsportPureTrace;
        return THEME_VERSIONS.BookingSport;
    } if (domain.includes(VERSIONS_URL.BookingFormation)) {
        return THEME_VERSIONS.BookingFormation;
    } if (domain.includes(VERSIONS_URL.Swisskischool)) {
        return THEME_VERSIONS.Swisskischool;
    }
    Sentry.captureMessage(`error getCurrentVersion : ${subdomain} - ${domain}`);
    return THEME_VERSIONS.Swisskischool;
};

export const goToDefaultDomain = () => {
    const {
        port, protocol, hostname, pathname
    } = window.location;
    const values = domaineBaseHasSubdomainAlready();

    const splited = hostname.split('.');

    let portpath = '';
    if (![80, 443].includes(Number(port))) {
        portpath = `:${port}`;
    }

    const tmpTable = pathname.split('/');
    const tmp = `/${tmpTable[1]}/`;
    window.location.href = `${protocol}//${values.baseDomainFunc(splited)}${portpath}${tmp}`;
};

// export const changeSubDomain = (subDomain) => {
//     const { hostname } = window.location;
//     const splited = hostname.split(".");
//     if (splited.length === 3)
// window.location.href = window.location.href.replace(splited[0], subDomain);
//     else window.location.href = window.location.href.replace(
// hostname, `${subDomain}.${hostname}`);
// }

export const changeSubDomain = (subDomain: string | undefined) => {
    const {
        pathname, protocol, hostname, port
    } = window.location;
    if (!subDomain) return;
    const values = domaineBaseHasSubdomainAlready();

    const splited = hostname.split('.');

    let portpath = '';
    if (![80, 443].includes(Number(port))) {
        portpath = `:${port}`;
    }

    const tmpTable = pathname.split('/');
    const tmp = `/${tmpTable[1]}/`;
    window.location.href = `${protocol}//${subDomain}.${values.baseDomainFunc(splited)}${portpath}${tmp}`;
};

/**
 * Convert a "#rrggbb" (or "#rgb") hex string to a comma-separated "r, g, b"
 * triplet, usable inside rgba(...). Returns null when the input isn't a hex
 * colour (e.g. "white", "transparent").
 */
const hexToRgbTriplet = (hex: string): string | null => {
    let value = (hex || '').trim().replace('#', '');
    if (value.length === 3) value = value.split('').map((c) => c + c).join('');
    if (!/^[0-9a-f]{6}$/i.test(value)) return null;
    return `${parseInt(value.slice(0, 2), 16)}, ${parseInt(value.slice(2, 4), 16)}, ${parseInt(value.slice(4, 6), 16)}`;
};

/**
 * Apply the correct theme corresponding to the current domain
 */
export const applyTheme = () => {
    const version = getCurrentThemeVersion();
    const theme = themes[version];
    const root = document.getElementById('body');
    Object.keys(theme).forEach((key) => {
        root?.style.setProperty(key, theme[key as keyof typeof theme]);
    });
    // Expose RGB triplets so stylesheets can build translucent tints with the
    // widely-supported rgba(var(--x-rgb), alpha) instead of the newer
    // rgb(from var(--x) ...) relative-colour syntax. Theme-adaptive: --ternary
    // is dark on light themes and light on dark themes, so chips keep contrast.
    const primaryRgb = hexToRgbTriplet(theme['--primary-color']);
    if (primaryRgb) root?.style.setProperty('--primary-color-rgb', primaryRgb);
    const ternaryRgb = hexToRgbTriplet(theme['--ternary-color']);
    if (ternaryRgb) root?.style.setProperty('--ternary-color-rgb', ternaryRgb);
};

export const getTheme = () => {
    const version = getCurrentThemeVersion();
    const theme = themes[version];
    return theme;
};
export const getLessonCardFromLesson = (lessons: School[], language: string) => lessons.map((lesson) => {
    const lessonCard = {
        id: lesson.id,
        name: getTranslation(lesson.translations, language)?.name,
        image: lesson.image,
        desc: getTranslation(lesson.translations, language)?.desc,
        path: lesson.path,
        translations: lesson.translations.map((t) => ({
            id: t.id, desc: t.desc, name: lesson.name, lang: t.lang
        }))

    };
    if (lessonCard.path === undefined || lessonCard.path === '') lessonCard.path = lesson.path;
    if (lessonCard.desc === undefined || lessonCard.desc === '') lessonCard.desc = lesson.desc ? lesson.desc : '';
    if (lessonCard.image === undefined || lessonCard.image === '') lessonCard.image = lesson.image;
    if (lessonCard.name === undefined || lessonCard.name === '') lessonCard.name = lesson.title;

    return lessonCard;
});
export const getLessonCardFromSchools = (schools: School[], language: string) => schools.map((school) => {
    const lessonCard = {
        id: school.id,
        name: school.name,
        image: school.image,
        desc: school.desc ? getTranslation(school.translations, language)?.desc : '',
        path: school.path,
        translations: school.translations.map((t) => ({
            id: t.id, desc: t.desc, name: school.name, lang: t.lang
        }))
    };
    if (lessonCard.path === undefined || lessonCard.path === '') lessonCard.path = school.path;
    if (lessonCard.desc === undefined || lessonCard.desc === '') lessonCard.desc = school.desc ? school.desc : '';
    if (lessonCard.image === undefined || lessonCard.image === '') lessonCard.image = school.image;
    if (lessonCard.name === undefined || lessonCard.name === '') lessonCard.name = school.title;

    return lessonCard;
});

export const getSnowliGroupKeyForSchool = () => {
    const school = Object.values(getSchoolsIds()).find((v) => v[0] === getSubDomain());

    if (school === undefined) {
        return undefined;
    }

    const schoolId = (school as any[])[1];

    const groupKey = SNOWLI_AI_SCHOOL_GROUP_KEYS[schoolId];
    return groupKey;
};

export const isNullOrUndefined = (input: any | null | undefined): input is null | undefined => {
    if (input === undefined || input === null) {
        return true;
    }

    // if (typeof input === 'string') {
    //     return input.trim().length === 0;
    // }

    // if (Array.isArray(input)) {
    //     return input.length === 0;
    // }

    // if (typeof input === 'object') {
    //     return Object.keys(input).length === 0;
    // }

    return false;
};

export const isNumber = (str: string | undefined) => {
    // Vérifie si la chaîne est vide ou ne contient que des espaces
    if (!str || str.trim() === '') return false;

    // Convertit la chaîne en nombre
    const num = Number(str);

    // Retourne false si ce n'est pas un nombre valide
    return !isNaN(num);
};

export const removeDuplicatesByKey = <T extends { [key: string]: any; }>(arr: T[], key: keyof T): T[] => {
    const map = new Map<number, T>();

    arr.forEach((item) => {
        if (!map.has(item[key])) {
            map.set(item[key], item);
        }
    });

    return Array.from(map.values());
};

export const removeInlineStyles = (htmlString: string) => htmlString.replace(/ style="[^"]*"/g, '');
export const stripHtml = (htmlString: string) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || '';
};

export const replaceHtmlTag = (htmlString: string, tagToReplace: string, replacementTag: string) => {
    // Create regex for opening and closing tags of the specified element
    const openingTagRegex = new RegExp(`<${tagToReplace}>`, 'g');
    const closingTagRegex = new RegExp(`</${tagToReplace}>`, 'g');

    // Replace specified tag with the replacement tag
    const replacedTags = htmlString
        .replace(openingTagRegex, `<${replacementTag}>`)
        .replace(closingTagRegex, `</${replacementTag}>`);

    return replacedTags;
};

export function isValidEmail(email: string): boolean {
    const emailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
}

export type RoundingMode =
    | 'nearest' // Math.round
    | 'up' // towards +infinity
    | 'down' // towards -infinity
    | 'towardsZero'
    | 'awayFromZero';

export type RoundNumberOptions = {
    /**
     * Nombre de décimales (par défaut 2).
     * Ignoré si `step` est défini.
     */
    decimals?: number;
    /**
     * Pas d'arrondi (ex: 0.05 pour le cash en Suisse).
     * Si défini, l'arrondi se fait au multiple le plus proche de ce pas.
     */
    step?: number;
    /**
     * Mode d'arrondi.
     * - nearest: au plus proche
     * - up: vers +inf
     * - down: vers -inf
     * - towardsZero / awayFromZero
     */
    mode?: RoundingMode;
};

function roundByMode(value: number, mode: RoundingMode): number {
    switch (mode) {
        case 'nearest':
            return Math.round(value);
        case 'up':
            return Math.ceil(value);
        case 'down':
            return Math.floor(value);
        case 'towardsZero':
            return value < 0 ? Math.ceil(value) : Math.floor(value);
        case 'awayFromZero':
            return value < 0 ? Math.floor(value) : Math.ceil(value);
        default:
            return Math.round(value);
    }
}

function countDecimals(input: number): number {
    if (!Number.isFinite(input)) return 0;
    // Handle scientific notation too (e.g. 1e-7)
    const s = input.toString().toLowerCase();
    if (s.includes('e-')) {
        const [, exp] = s.split('e-');
        const expNum = Number(exp);
        return Number.isFinite(expNum) ? expNum : 0;
    }
    const idx = s.indexOf('.');
    return idx === -1 ? 0 : (s.length - idx - 1);
}

function epsilonNudge(value: number): number {
    // A small value-scaled epsilon to avoid common floating rounding issues
    // (e.g. 249.005 -> 249.00 after binary representation).
    const sign = Math.sign(value) || 1;
    return sign * Number.EPSILON * (Math.abs(value) + 1);
}

/**
 * Arrondi robuste (limite les artefacts type 249.00000000000003).
 *
 * - Si `step` est fourni: arrondit au multiple de `step` (ex: 0.05).
 * - Sinon: arrondit à `decimals` décimales (par défaut 2).
 *
 * @example
 * roundNumber(249.00000000000003) -> 249
 * roundNumber(249.005) -> 249.01
 * roundNumber(1.03, { step: 0.05 }) -> 1.05
 */
export function roundNumber(value: number, options: RoundNumberOptions = {}): number {
    if (!Number.isFinite(value)) return value;

    const { step, decimals } = options;
    const mode: RoundingMode = options.mode ?? 'nearest';

    if (step !== undefined) {
        if (!Number.isFinite(step) || step <= 0) return value;

        const stepDecimals = countDecimals(step);
        const factor = 10 ** Math.min(15, stepDecimals);

        // Convert to scaled numbers to reduce floating errors.
        const scaledValue = (value + epsilonNudge(value)) * factor;
        const scaledStep = step * factor;
        if (scaledStep === 0) return value;

        const quotient = scaledValue / scaledStep;
        const roundedQuotient = roundByMode(quotient, mode);
        const result = (roundedQuotient * scaledStep) / factor;

        // Keep at most step precision to avoid artifacts like 1.0500000000000003
        return stepDecimals === 0 ? result : Number(result.toFixed(stepDecimals));
    }

    const decimalsToUse = decimals ?? 2;
    const safeDecimals = Math.max(0, Math.min(15, Math.trunc(decimalsToUse)));
    const factor = 10 ** safeDecimals;
    const scaled = (value + epsilonNudge(value)) * factor;
    const roundedScaled = roundByMode(scaled, mode);
    const result = roundedScaled / factor;
    return safeDecimals === 0 ? result : Number(result.toFixed(safeDecimals));
}

/**
 * @deprecated Utilise `roundNumber(value, { step, mode })`.
 */
export function roundToStep(
    value: number,
    step: number,
    mode: RoundingMode = 'nearest',
): number {
    return roundNumber(value, { step, mode });
}

/**
 * @deprecated Utilise `roundNumber(value, { step: 0.05, mode })`.
 */
export function roundToCashCHF(
    value: number,
    mode: RoundingMode = 'nearest',
): number {
    return roundNumber(value, { step: 0.05, mode });
}

/**
 * @deprecated Utilise `roundNumber(value, { decimals, mode })`.
 */
export function roundToDecimals(
    value: number,
    decimals: number = 2,
    mode: RoundingMode = 'nearest',
): number {
    return roundNumber(value, { decimals, mode });
}
